export interface Message {
  id: number;
  body?: string;
  direction: string;
  patient_id: number;
  therapist_id: number;
  read_at?: string;
  created_at: string;
  sender?: {
    id: number;
    first_name: string;
    last_name: string;
  };
  patient?: {
    id: number;
    first_name: string;
    last_name: string;
  };
  therapist?: {
    id: number;
    first_name: string;
    last_name: string;
  };
}

export interface MessagesResponse {
  items: Message[];
  metadata?: {
    pagination?: {
      has_next: boolean;
      cursor?: string;
    };
  };
}

export const MAX_PAGES = 100; // Safety limit to prevent infinite loops

// Sanitize CSV cells to prevent formula injection
// Cells starting with =, +, -, @, \t, or \r could trigger formula execution in Excel/Sheets
const sanitizeCell = (value: string): string => {
  if (!value) return value;
  const firstChar = value.charAt(0);
  if (firstChar === "=" || firstChar === "+" || firstChar === "-" || firstChar === "@" || firstChar === "\t" || firstChar === "\r") {
    return "'" + value; // Prefix with single quote to prevent formula execution
  }
  return value;
};

export const convertToCSV = (messages: Message[]): string => {
  const headers = ["Timestamp", "From", "To", "Direction", "Message", "Read At"];

  const rows = messages.map((msg) => {
    const from = msg.sender ? `${msg.sender.first_name} ${msg.sender.last_name}`.trim() : "";
    const to =
      msg.direction === "from_patient"
        ? msg.therapist
          ? `${msg.therapist.first_name} ${msg.therapist.last_name}`.trim()
          : ""
        : msg.patient
        ? `${msg.patient.first_name} ${msg.patient.last_name}`.trim()
        : "";

    // Sanitize first to prevent formula injection, then escape double quotes for CSV
    return [
      sanitizeCell(msg.created_at || "").replace(/"/g, '""'),
      sanitizeCell(from).replace(/"/g, '""'),
      sanitizeCell(to).replace(/"/g, '""'),
      sanitizeCell(msg.direction || "").replace(/"/g, '""'),
      sanitizeCell(msg.body || "").replace(/"/g, '""'),
      sanitizeCell(msg.read_at || "").replace(/"/g, '""'),
    ];
  });

  const csvContent = [headers.join(","), ...rows.map((row) => row.map((cell) => `"${cell}"`).join(","))].join("\n");

  return csvContent;
};

export const downloadCSV = (csv: string, filename: string) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
